import { UniteModel } from './unite.model';

class UniteService {
  unites: { [code: string]: UniteModel } = {};

  constructor () {
    this.registerUnitesMasse();
    this.registerUnitesVolume();
    this.registerUnitesLongueur();
    this.registerUnitesSurface();
    this.registerUnitesTemperature();
    this.registerUnitesMultiples();
  }

  private registerUnitesMasse () {
    // g, kg, tonne, quintal
    this.registerUnite(UniteModel.build('g', 'gramme', 'MASSE'));
    this.registerUnite(UniteModel.build('kg', 'kilogramme', 'MASSE', this.unites.g, 1000));
    this.registerUnite(UniteModel.build('t', 'tonne', 'MASSE', this.unites.kg, 1000));
    this.registerUnite(UniteModel.build('q', 'quintal', 'MASSE', this.unites.kg, 100));
  }

  private registerUnitesVolume () {
    // l, hl, m3, dal, dl, cl, ml
    this.registerUnite(UniteModel.build('m3', 'mètre cube', 'VOLUME'));
    this.registerUnite(UniteModel.build('l', 'litre', 'VOLUME', this.unites.m3, 0.001));
    this.registerUnite(UniteModel.build('hl', 'hectolitre', 'VOLUME', this.unites.l, 100));
    this.registerUnite(UniteModel.build('dal', 'décimalitre', 'VOLUME', this.unites.l, 10));
    this.registerUnite(UniteModel.build('dl', 'décilitre', 'VOLUME', this.unites.l, 0.1));
    this.registerUnite(UniteModel.build('cl', 'centilitre', 'VOLUME', this.unites.l, 0.01));
    this.registerUnite(UniteModel.build('ml', 'millilitre', 'VOLUME', this.unites.l, 0.001));
  }

  private registerUnitesLongueur () {
    // m, km, dm, cm, mm
    this.registerUnite(UniteModel.build('m', 'mètre', 'LONGUEUR'));
    this.registerUnite(UniteModel.build('km', 'kilomètre', 'LONGUEUR', this.unites.m, 1000));
    this.registerUnite(UniteModel.build('dm', 'décimètre', 'LONGUEUR', this.unites.m, 0.1));
    this.registerUnite(UniteModel.build('cm', 'centimètre', 'LONGUEUR', this.unites.m, 0.01));
    this.registerUnite(UniteModel.build('mm', 'millimètre', 'LONGUEUR', this.unites.m, 0.001));
  }

  private registerUnitesSurface () {
    // m2, are, ha
    this.registerUnite(UniteModel.build('m2', 'mètre carré', 'SURFACE'));
    this.registerUnite(UniteModel.build('are', 'are', 'SURFACE', this.unites.m2, 100));
    this.registerUnite(UniteModel.build('ha', 'hectare', 'SURFACE', this.unites.m2, 10000));
  }

  private registerUnitesTemperature () {
    // K, °C, °F
    this.registerUnite(UniteModel.build('K', 'Kelvin', 'TEMPERATURE'));
    this.registerUnite(UniteModel.build('°C', 'degré Celsius', 'TEMPERATURE', this.unites.K, (valeur: number) => valeur + 273.15, (valeur: number) => valeur - 273.15));
    this.registerUnite(UniteModel.build('°F', 'degré Fahrenheit', 'TEMPERATURE', this.unites.K, (valeur: number) => (valeur + 273.15) * 5 / 9 + 32, (valeur: number) => (valeur - 32) * 9 / 5 - 273.15));
  }

  private registerUnitesMultiples () {
    this.registerUnite(UniteModel.build('_', '_', 'MULTIPLES'));
    this.registerUnite(UniteModel.build('da_', 'deca', 'MULTIPLES', this.unites._, 10));
    this.registerUnite(UniteModel.build('h_', 'hecto', 'MULTIPLES', this.unites._, 100));
    this.registerUnite(UniteModel.build('k_', 'kilo', 'MULTIPLES', this.unites._, 1000));
    this.registerUnite(UniteModel.build('M_', 'mega', 'MULTIPLES', this.unites._, 1000000));
    this.registerUnite(UniteModel.build('G_', 'giga', 'MULTIPLES', this.unites._, 1000000000));
    this.registerUnite(UniteModel.build('T_', 'tera', 'MULTIPLES', this.unites._, 1000000000000));
    this.registerUnite(UniteModel.build('P_', 'peta', 'MULTIPLES', this.unites._, 1000000000000000));
    this.registerUnite(UniteModel.build('E_', 'exa', 'MULTIPLES', this.unites._, 1000000000000000000));
    this.registerUnite(UniteModel.build('Z_', 'zetta', 'MULTIPLES', this.unites._, 1000000000000000000000));
    this.registerUnite(UniteModel.build('Y_', 'yotta', 'MULTIPLES', this.unites._, 1000000000000000000000000));

    this.registerUnite(UniteModel.build('d_', 'deci', 'MULTIPLES', this.unites._, 0.1));
    this.registerUnite(UniteModel.build('c_', 'centi', 'MULTIPLES', this.unites._, 0.01));
    this.registerUnite(UniteModel.build('m_', 'milli', 'MULTIPLES', this.unites._, 0.001));
    this.registerUnite(UniteModel.build('µ_', 'micro', 'MULTIPLES', this.unites._, 0.000001));
    this.registerUnite(UniteModel.build('n_', 'nano', 'MULTIPLES', this.unites._, 0.000000001));
    this.registerUnite(UniteModel.build('p_', 'pico', 'MULTIPLES', this.unites._, 0.000000000001));
    this.registerUnite(UniteModel.build('f_', 'femto', 'MULTIPLES', this.unites._, 0.000000000000001));
    this.registerUnite(UniteModel.build('a_', 'atto', 'MULTIPLES', this.unites._, 0.000000000000000001));
    this.registerUnite(UniteModel.build('z_', 'zepto', 'MULTIPLES', this.unites._, 0.000000000000000000001));
    this.registerUnite(UniteModel.build('y_', 'yocto', 'MULTIPLES', this.unites._, 0.000000000000000000000001));
  }

  registerUnite (unite: UniteModel, override = false): void {
    if (this.unites[unite.code] && override === false) {
      throw new Error(`Unite ${unite.code} already registered`);
    }
    this.unites[unite.code] = unite;
  }

  getUnite (code: string): UniteModel {
    return this.unites[code];
  }

  convertion (valeur: number, uniteSource: UniteModel | string, uniteDestination: UniteModel | string): number {
    if (typeof uniteSource === 'string') {
      uniteSource = this.getUnite(uniteSource);
    }
    if (typeof uniteDestination === 'string') {
      uniteDestination = this.getUnite(uniteDestination);
    }

    if (uniteSource.type !== uniteDestination.type) {
      throw new Error(`Cannot convert ${uniteSource.code} to ${uniteDestination.code}, types are different`);
    }

    if (uniteSource.code === uniteDestination.code) {
      return valeur;
    }

    const uniteParente = this.getUniteParente(uniteSource, uniteDestination);

    const fonctionSourceVersParente = [];
    let u = uniteSource;
    while (u.code !== uniteParente.code) {
      fonctionSourceVersParente.push(u.convertionVersBase);
      u = u.uniteBase;
    }

    const fonctionParenteVersDestination = [];
    u = uniteDestination;
    while (u.code !== uniteParente.code) {
      fonctionParenteVersDestination.unshift(u.convertionDepuisBase);
      u = u.uniteBase;
    }

    let v = valeur;
    [].concat(fonctionSourceVersParente, fonctionParenteVersDestination).forEach(f => {
      v = f(v);
    });

    return v;
  }

  private getUniteParente (unite1: UniteModel, unite2: UniteModel): UniteModel {
    const treeU1 = this.getTree(unite1);
    const treeU2 = this.getTree(unite2);

    const uniteParente = treeU1.filter(u => treeU2.includes(u))[0];
    if (!uniteParente) {
      throw new Error(`No common parent found for ${unite1.code} and ${unite2.code}`);
    }
    return uniteParente;
  }

  private getTree (unite: UniteModel): UniteModel[] {
    const tree: UniteModel[] = [];
    while (unite) {
      tree.push(unite);
      unite = unite.uniteBase;
    }
    return tree;
  }
}

const uniteService = new UniteService();

export { uniteService };
